import type { PositionDecision, PositionRow } from './types.js';

/**
 * Flat multiple used only when no server verdict is available. trading_server applies a
 * per-symbol multiple (2.0 default, floored at 1.5) and its answer wins whenever we have it,
 * so the two never disagree on screen.
 */
const FALLBACK_STOP_ATR_MULTIPLIER = 1.8;

export const decidePosition = (row: Omit<PositionRow, 'decision'>): PositionDecision => {
    const { atr14, currentPrice, entryPrice, upsidePct, unrealizedPlPct } = row;
    const { stopLossBreached, stopLevel } = row;

    if (stopLossBreached === true) {
        return {
            kind: 'cut_loss',
            reasons: [
                stopLevel == null
                    ? 'Price below the server-computed stop level'
                    : `Price below the server-computed stop level ($${stopLevel.toFixed(2)})`,
            ],
        };
    }

    // Only guess when the server hasn't told us either way; an explicit `false` is authoritative.
    if (
        stopLossBreached == null &&
        atr14 !== null &&
        currentPrice < entryPrice - FALLBACK_STOP_ATR_MULTIPLIER * atr14
    ) {
        return { kind: 'cut_loss', reasons: ['Price >1.8×ATR below entry'] };
    }

    if (upsidePct !== null && unrealizedPlPct >= upsidePct) {
        return { kind: 'book_profit', reasons: ['Reached/exceeded expected upside'] };
    }

    if (upsidePct !== null && upsidePct - unrealizedPlPct <= 2 && unrealizedPlPct > 0) {
        return { kind: 'book_profit', reasons: ['Near expected upside target'] };
    }

    if (upsidePct !== null) {
        const remaining = Math.round((upsidePct - unrealizedPlPct) * 100) / 100;
        return { kind: 'stay', reasons: [`Remaining upside ~${remaining}%`] };
    }

    return { kind: 'stay', reasons: ['Hold'] };
};
